import {
  BarChart3,
  Boxes,
  CalendarClock,
  CalendarRange,
  Inbox,
  LayoutList,
  Library,
  MessagesSquare,
  Settings2,
  Sun,
  Target,
  User,
} from 'lucide-react'
import type { PersonRole } from '@netcon-work/shared'
import type { ComponentType } from 'react'

export interface NavEintrag {
  /** Pfad und gleichzeitig Ansichts-Kennung wie im Mockup (v-meintag → meintag). */
  id: string
  path: string
  label: string
  icon: ComponentType<{ className?: string }>
  /** Gruppentrenner in der Seitenleiste. */
  gruppe: 'arbeit' | 'steuerung' | 'fachbereich' | 'system'
  /** Wer den Punkt sieht – siehe {@link NAVIGATION}. */
  rollen: readonly PersonRole[]
  /**
   * **Gehört dieser Punkt zu einer Arbeitswelt?**
   * ([Z-07](../../../../docs/phasen/zusatz-07-workspaces.md))
   *
   * Gebundene Punkte stehen unter dem Umschalter und zeigen, was in **diesem** Workspace
   * liegt. Ungebundene stehen darüber oder darunter und gehen über alle: „Mein Tag" ist die
   * Ansicht des **Menschen**, und ein Mensch hat einen Tag.
   *
   * **Pflichtfeld, kein `?`.** Dieselbe Überlegung wie bei `rollen`: wer einen Punkt
   * hinzufügt, muss die Frage beantworten, weil das Feld sonst fehlt und TypeScript meckert.
   * Ein optionales Feld hätte jeden neuen Eintrag stillschweigend als „ungebunden" eingeordnet
   * – und ein ungebundener Punkt, der eigentlich gebunden gehört, zeigt fremde Arbeitswelten
   * durcheinander, ohne dass es jemandem auffällt.
   */
  workspaceGebunden: boolean
}

/** Kurzschreibweisen für die Rollenspalte – sonst steht dieselbe Aufzählung achtmal da. */
const TEAM = ['entwickler', 'steuerer', 'admin'] as const
const LEITUNG = ['steuerer', 'admin'] as const

/**
 * Navigation exakt in der Reihenfolge des Mockups (Konzept Kap. 5).
 * Eine Wahrheit für Seitenleiste, Router und ⌘K-Palette.
 *
 * ## Die Rollenspalte (24.08.2026)
 *
 * Nils, als Entwickler angemeldet: *„Die Menüpunkte, die der Entwickler eh nicht sehen darf,
 * die ausblenden."* Er sah den Leitstand in der Leiste, klickte, und bekam die richtige
 * Absage – eine Ansicht also, die nur dazu da war, ihn abzuweisen.
 *
 * Vorher standen hier drei Namenslisten (`NAV_FUER_GAST`, `NAV_FUER_MIT_OWNER`, „alle
 * anderen"), und die letzte war eine Restmenge: **jeder neue Eintrag war automatisch für
 * Entwickler sichtbar**, ohne dass irgendwo eine Entscheidung darüber gefallen wäre. Genau so
 * ist der Leitstand dort gelandet. Jetzt trägt jeder Eintrag seine Rollen selbst; wer einen
 * hinzufügt, muss die Frage beantworten, weil das Feld sonst fehlt und TypeScript meckert.
 *
 * ## Woher die Zuordnung kommt
 *
 * Aus dem, was der **Server** herausgibt – nicht aus dem Bauchgefühl:
 *
 * | Punkt | Rollen | Grund |
 * |---|---|---|
 * | Leitstand | Leitung | alle Abfragen sind `rollenProcedure('steuerer','admin')` ([E-19](../../../../docs/ENTSCHEIDUNGEN.md)) |
 * | KI & Einstellungen | Admin | Konzept Kap. 5 Punkt 9; sieben der neun Blöcke sind `rollenProcedure('admin')` |
 * | Mein Bereich | Fachbereich + Leitung | Konzept Kap. 5 Punkt 8 – die Ansicht des Mit-Owners; die Leitung behält sie für Abnahmen |
 * | alles Übrige | Team | `teamProcedure`, also auch für Entwickler |
 *
 * **Das bleibt Bedienbarkeit, keine Sicherheit.** Die Seitenleiste entscheidet, worauf jemand
 * klicken kann, nicht was er bekommt. Wer hier etwas hinzufügt, öffnet nichts; wer in
 * `teamProcedure`, `rollenProcedure` oder `aufgabenSichtbarkeit` etwas vergisst, ist mit dem
 * Weglassen hier nicht abgesichert.
 */
export const NAVIGATION: NavEintrag[] = [
  /**
   * **„Mein Tag" ist ungebunden** – die eine Ansicht, die über alle Workspaces geht
   * ([Z-07](../../../../docs/phasen/zusatz-07-workspaces.md)).
   *
   * Nils, 25.08.2026: *„Unter «Mein Tag» sind die des «Menschen» dann aber kombiniert."*
   * Ein Mensch hat einen Tag; dass seine Arbeit aus drei Arbeitswelten kommt, ist die Sache
   * der Organisation und nicht seine.
   */
  {
    id: 'meintag',
    path: '/',
    label: 'Mein Tag',
    icon: Sun,
    gruppe: 'arbeit',
    rollen: TEAM,
    workspaceGebunden: false,
  },
  /**
   * **My Work** ([Z-24](../../../../docs/phasen/zusatz-24-my-work.md),
   * [E-453](../../../../docs/ENTSCHEIDUNGEN.md#e-453)) – Aufgaben, Termine und Puls auf einer
   * Zeitachse. Zusätzlich zu „Mein Tag", nicht statt: von Nils am 23.10.2026 so beauftragt und
   * benannt. Ungebunden aus demselben Grund wie „Mein Tag" – ein Mensch hat einen Tag. **Hinter**
   * „Mein Tag", weil der erste Punkt auch das Ziel der Umleitung in `AppShell` ist.
   */
  {
    id: 'mywork',
    path: '/my-work',
    label: 'My Work',
    icon: CalendarClock,
    gruppe: 'arbeit',
    rollen: TEAM,
    workspaceGebunden: false,
  },
  {
    id: 'eingang',
    path: '/eingang',
    label: 'Eingang',
    icon: Inbox,
    gruppe: 'arbeit',
    rollen: TEAM,
    workspaceGebunden: false,
  },
  {
    id: 'alle',
    path: '/alle',
    label: 'Alle Aufgaben',
    icon: LayoutList,
    gruppe: 'arbeit',
    rollen: TEAM,
    workspaceGebunden: true,
  },

  /**
   * **Pools und Vorhaben sind Steuerung, nicht Arbeit** (Nils, 17.08.2026: *„Pools und
   * Vorhaben gehören für mich in die Steuerung."*).
   *
   * Das Mockup hatte sie oben einsortiert, weil ein Entwickler aus Pools zieht. Aber
   * *ziehen* tut er in „Mein Tag" – die Pool-**Ansicht** beantwortet eine andere Frage:
   * *wie voll ist es, und wo staut es sich?* Das ist eine Leitungsfrage.
   *
   * Die **Gruppe** ist trotzdem keine Rollenaussage: Pools und Planung stehen unter
   * „Steuerung" und sind für Entwickler offen – wer aus einem Pool zieht, darf sehen, wie
   * voll er ist.
   */
  {
    id: 'leitstand',
    path: '/leitstand',
    label: 'Leitstand',
    icon: BarChart3,
    gruppe: 'steuerung',
    rollen: LEITUNG,
    workspaceGebunden: true,
  },
  {
    id: 'pools',
    path: '/pools',
    label: 'Pools',
    icon: Boxes,
    gruppe: 'steuerung',
    rollen: TEAM,
    workspaceGebunden: true,
  },
  {
    id: 'vorhaben',
    path: '/vorhaben',
    label: 'Vorhaben',
    icon: Target,
    gruppe: 'steuerung',
    rollen: [...TEAM, 'mit_owner'],
    workspaceGebunden: true,
  },
  {
    id: 'planung',
    path: '/planung',
    label: 'Planung',
    icon: CalendarRange,
    gruppe: 'steuerung',
    rollen: TEAM,
    workspaceGebunden: true,
  },
  /**
   * **Die Ablage** ([Z-14](../../../../docs/phasen/zusatz-14-ablage.md), 11.09.2026) –
   * Vorlagen, Regeln und Dateien dieser Arbeitswelt, als Baum mit Fassungen.
   *
   * Gebunden, weil die Ablage der Entwicklung mit der des Marketings nichts zu tun hat; unter
   * „Steuerung", weil sie das ist, was das Team sich als Rahmen gibt. Für das **Team**, nicht
   * den Mit-Owner – von Nils bestätigt (F-82, 11.09.2026).
   */
  {
    id: 'ablage',
    path: '/ablage',
    label: 'Ablage',
    icon: Library,
    gruppe: 'steuerung',
    rollen: TEAM,
    workspaceGebunden: true,
  },

  /**
   * **Mein Bereich gehört dem Fachbereich** (Konzept Kap. 5 Punkt 8) – Melden, Abnahmen,
   * Anfragen. Die Leitung steht mit drin, weil der Zähler „wartende Abnahmen" an diesem
   * Punkt hängt und eine Abnahme sonst nur der bemerkt, der ohnehin darauf wartet.
   */
  {
    id: 'bereich',
    path: '/bereich',
    label: 'Mein Bereich',
    icon: User,
    gruppe: 'fachbereich',
    rollen: ['mit_owner', ...LEITUNG],
    workspaceGebunden: true,
  },

  /**
   * **Ein Gast hat genau einen Punkt** (18.08.2026,
   * [E-130](../../../../docs/ENTSCHEIDUNGEN.md)).
   *
   * Jede andere Ansicht zeigte ihm Leeres: Pools, Eingang, Leitstand und Planung sind
   * Teamarbeit, und der Server gibt ihm dort ohnehin nichts.
   *
   * **Das ist keine zehnte Ansicht im Sinne der UI-Referenz** (CLAUDE.md): sie erscheint
   * ausschließlich für Gäste. Die neun Ansichten des Mockups bleiben für alle anderen Rollen
   * unverändert – aufgeteilt auf die, die sie angehen.
   */
  {
    id: 'beteiligt',
    path: '/beteiligt',
    label: 'Meine Beteiligungen',
    icon: MessagesSquare,
    gruppe: 'arbeit',
    rollen: ['gast'],
    workspaceGebunden: false,
  },

  {
    id: 'einstellungen',
    path: '/einstellungen',
    label: 'KI & Einstellungen',
    icon: Settings2,
    gruppe: 'system',
    rollen: ['admin'],
    workspaceGebunden: false,
  },
]

/** Die Navigation, die zu dieser Rolle gehört. */
export function navigationFuer(role: PersonRole): NavEintrag[] {
  return NAVIGATION.filter((e) => e.rollen.includes(role))
}

/**
 * Steht dieser Punkt für diese Rolle in der Leiste?
 *
 * Gedacht für Abfragen, die **nur** einen solchen Punkt füttern: der Rückstandszähler an
 * „KI & Einstellungen", der Abnahmezähler an „Mein Bereich". Eine Abfrage, deren Ergebnis
 * nirgends erscheint, ist entweder Verkehr ohne Zweck oder – bei rollengeschützten
 * Prozeduren – ein `FORBIDDEN` in der Konsole bei jedem Seitenaufruf.
 */
export function siehtAnsicht(role: PersonRole, id: string): boolean {
  return NAVIGATION.some((e) => e.id === id && e.rollen.includes(role))
}

export const GRUPPEN_TITEL: Record<NavEintrag['gruppe'], string | null> = {
  arbeit: null,
  steuerung: 'Steuerung',
  fachbereich: 'Fachbereich',
  system: 'System',
}
